import { forEach, get, isNil, has, defaultTo, set, omitBy, mapValues, first, pickBy, flatten, includes } from 'lodash';
import {
  ClientActionHandlersConfig,
  ClientActionHandler,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { ARRAY_EDITOR_COLUMN_ID_PREFIX, STYLE_COLOR_ID, STYLE_ID } from 'src/utils/Domain/Constants';
import {
  CellEditingStartedEvent,
  ColDef,
  ColGroupDef,
  Column,
  GridApi,
  IRowNode,
  MenuItemDef,
} from '@ag-grid-community/core';
import { BasicItem } from 'src/types/Scope';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { ConfigurablePostAction, MassEditCoordinateMap } from 'configuration-schemas/viewdefns/general';
import { AsyncCellState } from '../ConfigurableGrid.types';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { EditCoordinates } from 'src/dao/pivotClient';
import { coalesce } from 'src/utils/Functions/Coalesce';

// Comparison variance/attainment percentage support starts here.
const COMPARISON_VARIANCE_RENDERERS = ['percent', 'percentOneDecimal', 'simplePercent'];

export function isSupportedComparisonVarianceRenderer(renderer: string | undefined): boolean {
  return (
    renderer != null &&
    COMPARISON_VARIANCE_RENDERERS.includes(renderer as (typeof COMPARISON_VARIANCE_RENDERERS)[number])
  );
}

function calculateComparisonPercentage(value: unknown, comparisonValue: unknown): number | undefined {
  if (value == null || comparisonValue == null) {
    return undefined;
  }

  const numericValue = Number(value);
  const numericComparisonValue = Number(comparisonValue);
  if (!Number.isFinite(numericValue) || !Number.isFinite(numericComparisonValue) || numericComparisonValue === 0) {
    return undefined;
  }

  return numericValue < numericComparisonValue
    ? (numericValue - numericComparisonValue) / numericComparisonValue
    : numericValue / numericComparisonValue;
}

export function parseActions(config: ClientActionHandlersConfig): ClientActionHandler {
  const retObj: ClientActionHandler = {};

  forEach(config, (actionValue, _action) => {
    // at this point the name of the action doesn't really matter only the clientHandler name and value (string[])
    forEach(actionValue, (attributesToHandle, clientHandler) => {
      retObj[clientHandler] = attributesToHandle;
    });
  });

  return retObj;
}

export function isGroupColDef(colDef: ColDef | ColGroupDef): colDef is ColGroupDef {
  return has(colDef, 'groupId');
}

export function isGroupNode(node: IRowNode): boolean {
  return !isNil(node.allChildrenCount) && node.allChildrenCount > 0;
}
export function isNotGroupNode(node: IRowNode): boolean {
  return !isGroupNode(node);
}

function isRowNode(rn: unknown): rn is IRowNode {
  const typedRowNode = rn as IRowNode;
  return 'parent' in typedRowNode && 'data' in typedRowNode;
}

export function updateWithClientHandler(field: string, clientHandlers: ClientActionHandler | undefined): boolean {
  if (isNil(clientHandlers)) {
    return false;
  }

  let handleWithClientHandler = false;
  forEach(clientHandlers, (attributesToHandle, _clientHandler) => {
    if (attributesToHandle.indexOf(field) >= 0) {
      handleWithClientHandler = true;
    }
  });

  return handleWithClientHandler;
}

export function calculateColumnWidth(dataIndex: string): number {
  switch (dataIndex) {
    case 'popoverTrigger':
      return 60;
    case 'attribute:ccstylecolorcreatedate:id':
      return 215;
    default:
      return 200;
  }
}

export function resetAsyncValidationData(event: CellEditingStartedEvent) {
  const { node } = event;
  if (has(node.data, 'invalid:style')) {
    const resetData = { ...node.data };
    delete resetData['invalid:style'];
    node.setData(resetData);
  }
}

export const floorsetDataToDropdown = (floorsetData: BasicItem): TenantConfigViewItem => {
  return {
    ...floorsetData,
    dataIndex: floorsetData.id,
    text: floorsetData.name,
  };
};

export const parseFloorsetDropdownData = (obj: BasicItem[]) => {
  // Currently, floorset returns data that needs to be mutated (id => dataIndex and name => text) for subheader
  return obj.map(floorsetDataToDropdown);
};

/** Converts an action from ConfigurablePostAction shape to MenuItemDef shape */
export const formatConfigurableActionItem = (
  action: ConfigurablePostAction,
  actionHandler: (updateType: 'coarse' | 'granular', value: unknown, valueDataIndex: string) => void,
): MenuItemDef => {
  const { name, icon, tooltip, updateType, value, valueDataIndex } = action;
  return {
    name,
    icon,
    tooltip,
    action: actionHandler.bind(null, updateType, value, valueDataIndex),
  };
};

export function refreshGridCells(
  gridApi: GridApi,
  rowNodes: IRowNode[],
  columns: (string | Column)[],
  options?: { forceRefresh: boolean },
) {
  const force = defaultTo(options?.forceRefresh, true);
  gridApi.refreshCells({
    rowNodes,
    columns,
    force,
  });
  gridApi.refreshHeader();
}

export function refreshRowCells(
  gridApi: GridApi,
  rowCells: Map<IRowNode, (string | Column)[]>,
  options?: { forceRefresh: boolean },
) {
  const force = defaultTo(options?.forceRefresh, true);
  for (let [key, columns] of rowCells) {
    gridApi.refreshCells({
      rowNodes: [key],
      columns,
      force,
    });
  }
  gridApi.refreshHeader();
}

function setGroupNodeAsyncState(
  node: IRowNode,
  fields: string[],
  state: AsyncCellState,
  updateChildren: boolean,
): void {
  fields.forEach((field) => {
    set(node, `data.asyncstate.${field}`, state);
  });
  if (updateChildren) {
    node.allLeafChildren.forEach((leafNode) => {
      fields.forEach((field) => {
        set(leafNode, `data.asyncstate.${field}`, state);
      });
    });
  }
}

function setRowNodeAsyncState(node: IRowNode, fields: string[], state: AsyncCellState, updateChildren: boolean): void {
  fields.forEach((field) => {
    set(node, `data.asyncstate.${field}`, state);
  });
  if (updateChildren) {
    node.childrenAfterFilter?.forEach((childNode) => {
      fields.forEach((field) => {
        set(childNode, `data.asyncstate.${field}`, state);
        childNode.setDataValue(`asyncstate.${field}`, state);
      });
    });
  }
}

function setRowNodesAsyncState(nodes: IRowNode[], fields: string[], state: AsyncCellState): void {
  fields.forEach((field) => {
    nodes.forEach((node) => {
      set(node, `data.asyncstate.${field}`, state);
    });
  });
}

export function getRowCellsByState(nodes: IRowNode[], state: AsyncCellState) {
  let cellMap = new Map<IRowNode, (string | Column)[]>();
  nodes.forEach((node) => {
    const fieldsInState = Object.keys(pickBy(get(node.data, 'asyncstate'), (v, k) => v === state));
    cellMap.set(node, fieldsInState);
  });
  return cellMap;
}
export function setRowCellsAsyncState(rowCells: Map<IRowNode, (string | Column)[]>, state: AsyncCellState): void {
  for (let [node, columns] of rowCells) {
    columns.forEach((field) => {
      set(node, `data.asyncstate.${field}`, state);
    });
  }
}

function setItemAsyncState(
  item: BasicPivotItem,
  fields: string[],
  state: AsyncCellState,
  updateChildren: boolean,
): void {
  fields.forEach((field) => {
    set(item, `asyncstate.${field}`, state);
  });
  if (updateChildren) {
    item.children.forEach((childItem) => {
      fields.forEach((field) => {
        set(childItem, `asyncstate.${field}`, state);
      });
    });
  }
}

export function updateNodeAsyncState(
  node: IRowNode | BasicPivotItem,
  field: string[],
  state: AsyncCellState,
  options?: { updateNodeChildren: boolean },
) {
  const updateChildren = defaultTo(options?.updateNodeChildren, false);
  if (isRowNode(node)) {
    if (isGroupNode(node)) {
      setGroupNodeAsyncState(node, field, state, updateChildren);
    } else {
      setRowNodeAsyncState(node, field, state, updateChildren);
    }
  } else {
    setItemAsyncState(node, field, state, updateChildren);
  }
}

export function updateAsyncState(nodes: IRowNode[], fields: string[], state: AsyncCellState) {
  setRowNodesAsyncState(nodes, fields, state);
}

/** data key holding a row's edit sequence, stored on row data like `asyncstate` */
export const ROW_EDIT_SEQUENCE_KEY = '$EDIT_SEQUENCE';

export function getRowEditSequence(node: IRowNode): number {
  return get(node.data, ROW_EDIT_SEQUENCE_KEY, 0);
}

/**
 * Bumps each row's edit sequence to latest sequence count.
 * Edits bump their row before the edit's api call fires; a redecorate stamps its captured
 * sequence on the rows it applies to so older in-flight redecorates skip those rows.
 */
export function updateRowEditSequenceFactory() {
  let incr = 0;
  return (nodes: IRowNode[], collectNodes: (nodes: IRowNode[]) => IRowNode[]): void => {
    incr++;
    const allNodesToUpdate = collectNodes(nodes);
    allNodesToUpdate.forEach((node) => set(node, `data.${ROW_EDIT_SEQUENCE_KEY}`, incr));
  };
}

export function isMultiNodeUpdate(nodes: IRowNode[]) {
  return nodes.length > 1;
}

export function isGroupNodeUpdate(nodes: IRowNode[]) {
  return nodes.length === 1 && isGroupNode(nodes[0]);
}

/**
 * Generate a set of coordinates,
 * filtering out nils for the set only having a subset of avail props.
 */
export function generateCoordinateValues(
  coordinateMap: MassEditCoordinateMap,
  dataItem: BasicPivotItem | IRowNode,
): EditCoordinates {
  const item = isRowNode(dataItem) ? dataItem.data : dataItem;
  return omitBy(
    mapValues(coordinateMap, (v) => item[v]),
    isNil,
  );
}

/**
 * Strips down the `dataItem` to a set of values based on the supplied function arguments.
 * `redecorateMap` takes precedence over `onlySendCoordinateMap` if both are configured.
 * If neither are configured, only UI generated data keys will be stripped including array editor data, asyncstate, and calc state data
 *
 * @param coordinates generated coordinate values based on `updateCoordinateMap` configuration
 * @param redecorateMap optional set of key/values that configurer can provide to make sure redecorate submits explicit data that is different than what is in the `updateCoordinateMap` configuration
 * @param onlySendCoordinateMap optional flag that configurer can set to only send values setup in the `updateCoordinateMap` configuration
 * @param dataItem item to retrieve data from for submitting to redecorate
 * @param leafIdProp
 * @returns BasicPivotItem
 */
export function filterItemForRedecorate(
  coordinates: EditCoordinates,
  redecorateMap: Record<string, string> | undefined,
  onlySendCoordinateMap: boolean,
  dataItem: BasicPivotItem,
  leafIdProp: string,
): BasicPivotItem {
  if (redecorateMap) {
    const redecorateMapWithId = {
      ...redecorateMap,
      [leafIdProp]: leafIdProp, // leafIdProp is needed to merge item back into data after redecorate
    };

    const onlyRedecorateMapKeys = omitBy(
      mapValues(redecorateMapWithId, (v) => dataItem[v]),
      isNil,
    ) as BasicPivotItem;

    return onlyRedecorateMapKeys;
  } else if (onlySendCoordinateMap) {
    return { ...coordinates } as BasicPivotItem;
  } else {
    // strip out array editor keys that were storing old values and preventing the header editor value from being applied after redecorate
    // also remove other custom ui keys

    const withoutLocalKeys = omitBy(dataItem, (_v, k) => {
      return (
        k.includes(ARRAY_EDITOR_COLUMN_ID_PREFIX) ||
        k.includes('asyncstate') ||
        k.includes('__calc_editted') ||
        k === ROW_EDIT_SEQUENCE_KEY
      );
    }) as BasicPivotItem;

    return withoutLocalKeys;
  }
}

export function getCellAsyncState(node: IRowNode, colInfo: { dataIndex: string }) {
  return get(node.data, `asyncstate.${colInfo.dataIndex}`, AsyncCellState.Idle);
}

export function getDataKeyAsyncState(data: Record<string, unknown>, dataIndex: string) {
  return get(data, `asyncstate.${dataIndex}`, AsyncCellState.Idle) as AsyncCellState;
}

function getAllFirstLeafs(
  node: IRowNode,
  api: GridApi,
  comparisonDataIndex: string,
  aggableGroups?: string[],
): (number | null)[] {
  if (node.leafGroup) {
    let rawCompValue: number | null = null;
    const firstLeafNode = first(node.allLeafChildren);
    if (isNil(firstLeafNode)) rawCompValue = null;
    else
      rawCompValue = coalesce(
        api.getValue(comparisonDataIndex, firstLeafNode),
        get(firstLeafNode.data, comparisonDataIndex),
      );
    return [rawCompValue];
  } else {
    const children = node.childrenAfterSort ?? [];
    return children
      .filter((c, index) => {
        if (isNil(aggableGroups) || includes(aggableGroups, c.field ?? '')) {
          return true;
        } else {
          return index == 0;
        }
      })
      .map((c) => {
        const newLeafs = getAllFirstLeafs(c, api, comparisonDataIndex, aggableGroups);
        return newLeafs;
      })
      .reduce((acc, leafs) => {
        return acc.concat(leafs);
      }, []);
  }
}

/** Took this logic from editable grid value getter so it can be used there & from onExcelExport */
export function buildComparisonCellValue({
  value,
  comparisonDataIndex,
  node,
  api,
  renderJustValue,
  comparisonRenderJustValue,
  renderVarianceValue,
  aggableGroups,
}: {
  value: unknown;
  comparisonDataIndex: string;
  aggableGroups?: string[];
  node: IRowNode;
  api: GridApi | null;
  renderJustValue: ((v: unknown) => string) | undefined;
  comparisonRenderJustValue?: (v: unknown) => string;
  renderVarianceValue?: (v: number) => string;
}): string {
  const format = (v: unknown, renderer = renderJustValue): string | undefined => {
    if (isNil(v) || Number.isNaN(v)) return undefined;
    return renderer ? renderer(v) : String(v);
  };
  const renderedValue = format(value);
  if (isNil(api)) {
    return '';
  } else if (!includes(aggableGroups, node.field ?? '')) {
    return renderedValue ?? '';
  }
  // First check to see if there is a defined column for the comparison index
  const gridHasColDef = api.getAllGridColumns().some((c) => c.getColId() === comparisonDataIndex);
  let rawCompValue: unknown;
  // If there is, use its value at aggregate
  if (gridHasColDef) {
    rawCompValue = api.getValue(comparisonDataIndex, node);
  } else {
    // Use first child node in cases where there is no configured column. This create a simplified
    // default for cases like target compare where every row has the same value.
    if (node.leafGroup) {
      const firstLeafNode = first(node.allLeafChildren);
      if (isNil(firstLeafNode)) rawCompValue = null;
      else
        rawCompValue = coalesce(
          api.getValue(comparisonDataIndex, firstLeafNode),
          get(firstLeafNode.data, comparisonDataIndex),
        );
    } else {
      // calc via group
      let rowsToUse = getAllFirstLeafs(node, api, comparisonDataIndex, aggableGroups);
      // allow more generic agg
      rawCompValue = rowsToUse.reduce((a: number, i) => (i != null ? a + i : a), 0);
    }
  }

  const renderedCompValue = format(rawCompValue, comparisonRenderJustValue ?? renderJustValue) ?? '';
  const comparisonPercentage = calculateComparisonPercentage(value, rawCompValue);
  const renderedVarianceValue = renderVarianceValue ? renderVarianceValue(comparisonPercentage ?? 0) : '';

  const comparisonValue = !isNil(renderedValue) ? `${renderedValue} │ ${renderedCompValue}` : ` │ ${renderedCompValue}`;
  return renderVarianceValue ? `${comparisonValue} │ ${renderedVarianceValue}` : comparisonValue;
}

/** Helper for grabbing hardcoded values off of Pivot Items */
export const extractStyleIdFromPivotItem = (item: BasicPivotItem): string | undefined =>
  item[STYLE_ID] ?? item['style'];
export const extractStyleColorIdFromPivotItem = (item: BasicPivotItem): string | undefined =>
  item[STYLE_COLOR_ID] ?? item['stylecolor'];

export const matchStyleOrStyleColorToItem = (id: string, item: BasicPivotItem) => {
  // prettier-ignore
  return item[STYLE_ID] === id ||
        item[STYLE_COLOR_ID] === id ||
        item['style'] === id ||
        item['stylecolor'] === id;
};
